/**
 * INR → foreign currency conversion for Small Vendors overseas checkout only.
 * Uses open.er-api.com (INR base) — not used on Overseas Donor or other pages.
 */

/** Open ER API — vendor payment page only; supports USD, AED, GBP, etc. */
const EXCHANGE_API_URL = "https://open.er-api.com/v6/latest/INR";
const CACHE_TTL_MS = 5 * 60 * 1000;
let ratesCache = null;
let ratesFetchedAt = 0;

const getInrRates = async () => {
  if (ratesCache && Date.now() - ratesFetchedAt < CACHE_TTL_MS) {
    return ratesCache;
  }
  const res = await fetch(EXCHANGE_API_URL);
  if (!res.ok) {
    throw new Error("Unable to fetch exchange rate. Please try again.");
  }
  const data = await res.json();
  if (data?.result !== "success" || !data?.rates) {
    throw new Error("Exchange rate unavailable. Please try again.");
  }
  ratesCache = data.rates;
  ratesFetchedAt = Date.now();
  return ratesCache;
};

/**
 * @param {number} inrAmount - amount in INR (products only for overseas vendor)
 * @param {string} toCurrency - ISO currency code (USD, GBP, …)
 * @returns {Promise<{ inrAmount: number, amount: number, rate: number, currency: string }>}
 */
export const convertInrToCurrency = async (inrAmount, toCurrency) => {
  const inr = Number(inrAmount) || 0;
  const target = String(toCurrency || "USD").toUpperCase();

  if (target === "INR" || inr <= 0) {
    return { inrAmount: inr, amount: inr, rate: 1, currency: "INR" };
  }

  const rates = await getInrRates();
  const rate = rates[target];
  if (!rate || typeof rate !== "number") {
    throw new Error(`Exchange rate unavailable for ${target}.`);
  }

  const amount = Math.round(inr * rate * 100) / 100;
  return { inrAmount: inr, amount, rate, currency: target };
};

export const formatConvertedAmount = (amount, currencyCode) => {
  const code = String(currencyCode || "USD").toUpperCase();
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: value < 10 ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${code} ${value.toFixed(2)}`;
  }
};
